import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { EMPTY_STRING, FORMAT_DATE_FOR_LOG } from 'common/constants';
import { getUserType } from 'common/utils/index';
import { useUser, useIsMapsClient } from 'store/features/auth/selectors/user';
import { useIntl } from 'react-intl';
import {
  getAccessToken,
  getLastAccessToken,
  getIsIdentifyToken,
} from 'common/utils/auth/accessToken';
import { getCountRefreshPage } from 'common/utils/auth/countRefresgPage';

interface TrackEvent {
  operation: string;
  data: Record<string, any>;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const ENDPOINT_ID = 'e1-card.locator';

export const useMindbox = () => {
  const isReady = useRef(!!getAccessToken());
  const location = useLocation();
  const { locale } = useIntl();
  const { login = EMPTY_STRING } = useUser() || {};
  const { isMapsClient } = getUserType();
  const isMapsClientStore = useIsMapsClient();

  const trackEvent = useCallback(
    ({ operation, data, onSuccess, onError }: TrackEvent) => {
      const accessToken = getAccessToken();
      if (
        !accessToken ||
        !(isMapsClient || isMapsClientStore) ||
        !isReady.current
      ) {
        return;
      }
      window.mindbox('async', {
        operation,
        data,
        onSuccess,
        onError,
      });
    },
    [isMapsClient, isMapsClientStore],
  );

  const trackLocatorLanguage = useCallback(
    (locale: string) => {
      trackEvent({
        operation: 'LocatorLanguage',
        data: {
          customer: {
            customFields: {
              locatorLanguage: locale,
            },
            ids: {
              loginClientsPanel: login,
            },
          },
        },
        onSuccess: () => {
          console.info(
            `trackLocatorLanguage mindbox успешно отработал в ${moment().format(
              FORMAT_DATE_FOR_LOG,
            )}.`,
          );
        },
        onError: () => (e: any) => {
          console.info(
            `Ошибка при вызове trackLocatorLanguage в  ${moment().format(
              FORMAT_DATE_FOR_LOG,
            )}: `,
            e,
          );
        },
      });
    },
    [login, trackEvent],
  );

  const trackLocatorAutorization = useCallback(async () => {
    const accessToken = getAccessToken();
    const lastAccessToken = await getLastAccessToken();
    const countRefreshPage = getCountRefreshPage();
    const isIdentifyToken = getIsIdentifyToken();
    const isCheckRefreshPage = isIdentifyToken
      ? countRefreshPage === 0
      : countRefreshPage === 1;

    const isTrack =
      accessToken !== lastAccessToken ||
      (accessToken === lastAccessToken && isCheckRefreshPage);

    if (isTrack) {
      trackEvent({
        operation: 'LocatorAutorization',
        data: {
          customer: {
            ids: {
              loginClientsPanel: login,
            },
            customFields: {
              locatorLanguage: locale,
            },
          },
        },
        onSuccess: () => {
          console.info(
            `trackLocatorAutorization mindbox успешно отработал в ${moment().format(
              FORMAT_DATE_FOR_LOG,
            )}.`,
          );
        },
        onError: () => (e: any) => {
          console.info(
            `Ошибка при вызове trackLocatorAutorization в  ${moment().format(
              FORMAT_DATE_FOR_LOG,
            )}: `,
            e,
          );
        },
      });
    }
  }, [login, trackEvent, locale]);

  const initializeMindbox = useCallback(() => {
    const accessToken = getAccessToken();

    if (!accessToken || !(isMapsClient || isMapsClientStore)) {
      return;
    }

    if (window.mindbox) {
      isReady.current = true;
      return;
    }

    window.PopMechanic = { watchLocation: false };

    try {
      window.mindbox = function (...args: any[]) {
        window.mindbox.queue.push(args);
      };
      window.mindbox.queue = [];
      window.mindbox('create', { endpointId: ENDPOINT_ID });

      const script = document.createElement('script');
      script.src = `https://api.mindbox.ru/scripts/v1/tracker.js`;
      script.async = true;
      document.head.appendChild(script);
      script.onload = () => {
        isReady.current = true;
        trackLocatorAutorization();
      };
    } catch (e) {
      console.info('Error loading script mindbox: ', e);
    }
  }, [isMapsClient, isMapsClientStore, trackLocatorAutorization]);

  useEffect(() => {
    initializeMindbox();
  }, []);

  useEffect(() => {
    if ((isMapsClient || isMapsClientStore) && window?.PopMechanic?.update) {
      window.PopMechanic.update();
    }
  }, [location.pathname, isMapsClient, isMapsClientStore]);

  return {
    isReady: isReady.current,
    trackEvent,
    trackLocatorAutorization,
    trackLocatorLanguage,
    initializeMindbox,
  };
};

export default useMindbox;
